import React from "react";
import landing from "@/content/landing";
import { Section } from "./HeroSection";

/**
 * Rein präsentative Vertrauensring-Vorschau (SVG lokal).
 * Wir vermeiden bewusst den Import der App-Komponente `TrustRing.jsx`, um
 * die Landingpage vollständig von Life-Core-Produktlogik zu entkoppeln.
 */
function RingSVG({ value = 68 }) {
  const size = 220;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto h-56 w-56 sm:h-64 sm:w-64" role="img" aria-label={`Vertrauensring · ${value} Prozent`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(15,23,42,0.08)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="url(#ag-ring-grad)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <defs>
        <linearGradient id="ag-ring-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F59E0B" />
          <stop offset="100%" stopColor="#B45309" />
        </linearGradient>
      </defs>
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fill="#0F172A"
        fontFamily="'Cormorant Garamond', Georgia, serif"
        fontSize="40"
        fontWeight="600"
        dangerouslySetInnerHTML={{ __html: `${value}%` }}
      />
    </svg>
  );
}

export default function TrustRingShowcase() {
  const t = landing.trustRing;
  return (
    <Section id="vertrauensring" className="bg-white" data-testid="landing-trustring">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-16">
        <div className="lg:col-span-5">
          <RingSVG value={t.demoValue} />
        </div>
        <div className="lg:col-span-7">
          <h2 className="font-serif text-3xl leading-tight text-ag-ink sm:text-4xl lg:text-5xl">
            {t.headline}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-[17px]">{t.text}</p>

          <dl className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {t.dimensions.map((d) => (
              <div
                key={d.key}
                className="rounded-xl border border-slate-200/70 bg-ag-paper p-4"
                data-testid={`landing-trustring-dim-${d.key}`}
              >
                <dt className="text-xs uppercase tracking-[0.18em] text-slate-500">{d.label}</dt>
                <dd className="mt-2 flex items-center gap-3">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-slate-800"
                      style={{ width: `${d.value}%` }}
                      aria-hidden="true"
                    />
                  </div>
                  <span className="text-sm font-medium text-slate-700">{d.value}%</span>
                </dd>
              </div>
            ))}
          </dl>

          <blockquote className="mt-8 border-l-2 border-amber-500 pl-5 font-serif text-lg italic text-slate-600 sm:text-xl">
            {t.quote}
          </blockquote>
        </div>
      </div>
    </Section>
  );
}
